import { StateCreator } from 'zustand'
import { parse, isValid, isWithinInterval, startOfMonth, endOfMonth } from 'date-fns'
import { UNIVERSAL_DATE_FORMAT } from '@dutchinvoice/shared-utils'
import { invoiceApi } from '../../lib/api'
import { useAuthStore } from '../authStore'
import { mergeInvoices } from '../../utils/invoiceMerge'
import type { InvoiceStore } from '../invoiceStore'
import type { SyncedInvoice } from '../../types/invoice'

// Helper functions for local storage
const getLocalInvoices = (): SyncedInvoice[] => {
    const stored = localStorage.getItem('dutchinvoice_local_invoices')
    return stored ? JSON.parse(stored) : []
}

const saveLocalInvoices = (invoices: SyncedInvoice[]) => {
    localStorage.setItem('dutchinvoice_local_invoices', JSON.stringify(invoices))
}

export interface InvoiceFilterSlice {
    // State
    currentPeriod: string
    isLoadingPeriod: boolean

    // Actions
    setCurrentPeriod: (period: string) => void
    getFilteredInvoicesByPeriod: (invoices: SyncedInvoice[]) => SyncedInvoice[]
    fetchInvoicesByPeriod: (period: string) => Promise<void>
}

export const createInvoiceFilterSlice: StateCreator<
    InvoiceStore,
    [],
    [],
    InvoiceFilterSlice
> = (set, get) => {
    // Set current month as default period
    const now = new Date()
    const defaultPeriod = `month-${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`

    return {
        // State
        currentPeriod: defaultPeriod,
        isLoadingPeriod: false,

        // Actions
        setCurrentPeriod: (period: string) => {
            set({ currentPeriod: period })

            // Remove the automatic fetch to prevent duplicate API calls
            // The fetch will be handled by the useEffect in InvoiceOverview.tsx
        },

        fetchInvoicesByPeriod: async (period: string) => {
            const { isAuthenticated } = useAuthStore.getState()

            // Only fetch from API if user is authenticated
            if (!isAuthenticated) {
                return
            }

            try {
                set({ isLoadingPeriod: true })

                // Fetch invoices with period filtering
                const response = await invoiceApi.getInvoices({
                    period: period,
                    // Remove the 'planned' filter as we're implementing a separate endpoint
                })

                // Get local invoices
                const localInvoices = getLocalInvoices()

                // Merge the fetched invoices with local invoices
                const mergedInvoices = mergeInvoices(response.invoices, localInvoices)

                // Update localStorage
                saveLocalInvoices(mergedInvoices)

                // Update the store with the merged invoices
                set({
                    savedInvoices: mergedInvoices,
                    isLoadingPeriod: false
                })
            } catch (error) {
                console.error('[Filter] Failed to fetch invoices by period:', error)
                set({ isLoadingPeriod: false })
            }
        },

        getFilteredInvoicesByPeriod: (invoices: SyncedInvoice[]) => {
            const { currentPeriod } = get()

            // If no period filter is set, return all invoices
            if (!currentPeriod) {
                return invoices
            }

            // For planned period, filter to show only template invoices (schedules) and future invoices
            if (currentPeriod === 'planned') {
                const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format

                return invoices.filter(invoice => {
                    // Exclude generated invoices (they are actual invoices, not schedule templates)
                    if (invoice.generatedFromScheduleId) {
                        return false;
                    }

                    // For drafts/concepts (no invoice number), only show if they have a scheduleId
                    if (!invoice.invoiceNumber) {
                        return !!invoice.scheduleId;
                    }

                    // For regular invoices, show if they have a future date or a scheduleId (but not generated)
                    const isFutureInvoice = invoice.date > today;
                    return isFutureInvoice || !!invoice.scheduleId;
                });
            }

            // For all users, filter by date
            // Parse the period value
            if (currentPeriod.startsWith('month-')) {
                // Month filter: month-YYYY-MM
                const [, yearStr, monthStr] = currentPeriod.split('-')
                const year = parseInt(yearStr, 10)
                const month = parseInt(monthStr, 10) - 1 // 0-indexed month

                if (isNaN(year) || isNaN(month)) {
                    console.error('[Filter] Invalid month period format:', currentPeriod)
                    return invoices
                }

                const periodStart = startOfMonth(new Date(year, month))
                const periodEnd = endOfMonth(new Date(year, month))

                return invoices.filter(invoice => {
                    // Skip drafts (no invoice number)
                    if (!invoice.invoiceNumber) return true

                    // Parse the invoice date (now in YYYY-MM-DD format)
                    const parsedDate = parse(invoice.date, UNIVERSAL_DATE_FORMAT, new Date())

                    if (!isValid(parsedDate)) {
                        console.warn('[Filter] Invalid date format for invoice:', invoice.invoiceNumber, invoice.date)
                        return false
                    }

                    return isWithinInterval(parsedDate, { start: periodStart, end: periodEnd })
                })
            } else if (currentPeriod.startsWith('custom-')) {
                // Custom date range: custom-YYYY-MM-DD-YYYY-MM-DD
                const parts = currentPeriod.split('-')

                // Ensure we have at least 7 parts for a valid custom range
                // Format: custom-YYYY-MM-DD-YYYY-MM-DD
                if (parts.length !== 7) {
                    console.error('[Filter] Invalid custom period format:', currentPeriod)
                    return invoices
                }

                const startDateStr = `${parts[1]}-${parts[2]}-${parts[3]}`
                const endDateStr = `${parts[4]}-${parts[5]}-${parts[6]}`

                const startDate = parse(startDateStr, UNIVERSAL_DATE_FORMAT, new Date())
                const endDate = parse(endDateStr, UNIVERSAL_DATE_FORMAT, new Date())

                if (!isValid(startDate) || !isValid(endDate)) {
                    console.error('[Filter] Invalid custom date range:', startDateStr, endDateStr)
                    return invoices
                }

                return invoices.filter(invoice => {
                    // Skip drafts (no invoice number)
                    if (!invoice.invoiceNumber) return true

                    // Parse the invoice date (now in YYYY-MM-DD format)
                    const parsedDate = parse(invoice.date, UNIVERSAL_DATE_FORMAT, new Date())

                    if (!isValid(parsedDate)) {
                        console.warn('[Filter] Invalid date format for invoice:', invoice.invoiceNumber, invoice.date)
                        return false
                    }

                    return isWithinInterval(parsedDate, { start: startDate, end: endDate })
                })
            }

            // If period format is not recognized, return all invoices
            console.warn('[Filter] Unrecognized period format:', currentPeriod)
            return invoices
        }
    }
} 